import { useCallback, useEffect, useRef, useState } from 'react';

import { TaskStatus } from 'src/enums/TaskStatus';
import TasksRepository from 'src/repositories/superAdmin/TasksRepository';
import TimeTrackingExportRepository, { ExportParams } from 'src/repositories/talents/TimeTrackingExportRepository';
import { isAxiosError, isNotFoundError } from 'src/utils/responseErrors';
import { getAttachmentFileName, saveBlob } from 'src/utils/saveBlob';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import useSnackbar from 'src/hooks/useSnackbar';

export enum ExportFileStatus {
  idle = 'idle',
  preparing = 'preparing',
  ready = 'ready',
  failed = 'failed',
}

export const POLL_INTERVAL_MS = 2000;
// a file that is not ready by then is reported as not made
export const WAIT_LIMIT_MS = 2 * 60 * 1000;
const FALLBACK_FILE_NAME = 'Time_Tracking.xlsx';

type PreparedFile = {
  blob: Blob;
  fileName: string;
};

type UseTimeTrackingExportFile = {
  status: ExportFileStatus;
  prepare: (params: ExportParams) => Promise<void>;
  save: () => void;
};

/**
 * One weekly file: the server makes it in the background, and once it is ready it is fetched as
 * data, so the browser never opens an address that could show an error page instead of the app.
 * With autoSave the file is saved as soon as it is ready; otherwise save() saves it on a click
 */
const useTimeTrackingExportFile = ({ autoSave }: { autoSave: boolean }): UseTimeTrackingExportFile => {
  const [status, setStatus] = useState<ExportFileStatus>(ExportFileStatus.idle);
  const fileRef = useRef<PreparedFile | null>(null);
  const isMountedRef = useIsMountedRef();
  const timerIdRef = useRef<number | null>(null);
  const { enqueueErrorSnackbar } = useSnackbar();

  useEffect(
    () => () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
    },
    [],
  );

  const wait = (milliseconds: number) =>
    new Promise<void>(resolve => {
      timerIdRef.current = window.setTimeout(resolve, milliseconds);
    });

  const getTaskStatus = async (taskId: string): Promise<TaskStatus> => {
    try {
      const {
        data: { data: task },
      } = await TasksRepository.show(taskId);
      return task.status;
    } catch (error: unknown) {
      // a task still waiting for a worker is not known yet
      if (isAxiosError(error) && isNotFoundError(error)) {
        return TaskStatus.STARTED;
      }
      throw error;
    }
  };

  const waitForTask = async (taskId: string): Promise<void> => {
    const startedAt = Date.now();
    for (;;) {
      // the page or the dialog is gone: nobody waits for the file any more
      if (!isMountedRef.current) throw new Error('The file is no longer wanted');
      // eslint-disable-next-line no-await-in-loop
      const taskStatus = await getTaskStatus(taskId);
      if (taskStatus === TaskStatus.SUCCESS) return;
      if (taskStatus === TaskStatus.FAILURE) throw new Error('The file could not be made');
      if (Date.now() - startedAt >= WAIT_LIMIT_MS) throw new Error('The file was not ready in time');
      // eslint-disable-next-line no-await-in-loop
      await wait(POLL_INTERVAL_MS);
    }
  };

  const save = useCallback(() => {
    if (fileRef.current) {
      saveBlob(fileRef.current.blob, fileRef.current.fileName);
    }
  }, []);

  const prepare = useCallback(
    async (params: ExportParams) => {
      fileRef.current = null;
      setStatus(ExportFileStatus.preparing);
      try {
        const { data: task } = await TimeTrackingExportRepository.create(params);
        await waitForTask(task.taskId);
        if (!isMountedRef.current) return;
        const { data: blob, headers } = await TasksRepository.file(task.taskId);
        if (!isMountedRef.current) return;

        fileRef.current = { blob, fileName: getAttachmentFileName(headers['content-disposition'], FALLBACK_FILE_NAME) };
        if (autoSave) {
          save();
          setStatus(ExportFileStatus.idle);
          return;
        }
        setStatus(ExportFileStatus.ready);
      } catch (error: unknown) {
        if (!isMountedRef.current) return;
        enqueueErrorSnackbar();
        setStatus(ExportFileStatus.failed);
      }
    },
    [autoSave],
  );

  return { status, prepare, save };
};

export default useTimeTrackingExportFile;
